<template>
  <!-- Mode Carte -->
  <div v-if="viewMode === 'card'"
    class="anamnesis-card relative hover:shadow-lg transition-shadow duration-200 flex flex-col min-h-0"
    :class="{ 'cursor-pointer': !selectionMode, 'selection-active': selected }"
    @click="handleCardClick">
    <!-- Checkbox de sélection -->
    <div v-if="selectable" class="absolute top-2 left-2 z-10" @click.stop>
      <input 
        type="checkbox"
        :checked="selected"
        class="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
        @change="emitToggleSelection"
      />
    </div>

    <!-- Type Icon with Tooltip - EN HAUT À GAUCHE -->
    <div class="absolute top-3 left-10 z-5" @mouseenter="isIconHovered = true" @mouseleave="isIconHovered = false">
      <component :is="getTypeIcon" v-if="getTypeIcon" class="h-5 w-5 text-gray-600 cursor-help" />
      <!-- Custom Tooltip -->
      <div v-if="isIconHovered"
        class="absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg z-10 whitespace-nowrap">
        {{ getSchemaTooltipText }}
      </div>
    </div>

    <!-- Badge de propriété - EN HAUT À DROITE -->
    <div class="absolute top-2 right-2 z-5">
      <!-- Affichage pour les assets appartenant au user : icône œil privé/public -->
      <div v-if="isOwned" class="relative group">
        <div v-if="!memo.is_public"
          class="w-6 h-6 rounded-full flex items-center justify-center bg-red-100 text-red-600">
          <EyeOffIcon class="w-4 h-4" />
        </div>
        <div v-else class="w-6 h-6 rounded-full flex items-center justify-center bg-green-100 text-green-600">
          <EyeIcon class="w-4 h-4" />
        </div>
        <!-- Tooltip -->
        <div class="anamnesis-tooltip">
          <div class="anamnesis-tooltip-content">
            {{ memo.is_public ? 'Visible par tous' : 'Visible par vous seulement' }}
          </div>
          <div class="anamnesis-tooltip-arrow"></div>
        </div>
      </div>
      <!-- Affichage pour les assets publics d'autres users -->
      <div v-else-if="memo.is_public" class="relative group">
        <div class="w-6 h-6 rounded-full flex items-center justify-center bg-green-100 text-green-600">
          <GlobeIcon class="w-4 h-4" />
        </div>
        <!-- Tooltip -->
        <div class="anamnesis-tooltip">
          <div class="anamnesis-tooltip-content">
            Mémo public
          </div>
          <div class="anamnesis-tooltip-arrow"></div>
        </div>
      </div>
    </div>

    <!-- Status Bubbles - SOUS LE BADGE DE PROPRIÉTÉ -->
    <div v-if="showStatusBubbles" class="absolute top-10 right-2 flex flex-col items-end gap-1">
      <div v-if="showStatusBadge.showDraft" class="relative group">
        <div class="w-6 h-6 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center">
          <FileEditIcon class="h-4 w-4" />
        </div>
        <!-- Tooltip -->
        <div class="anamnesis-tooltip">
          <div class="anamnesis-tooltip-content">Brouillon</div>
          <div class="anamnesis-tooltip-arrow"></div>
        </div>
      </div>
      <div v-if="showStatusBadge.showValidated" class="relative group">
        <div class="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center">
          <FileCheckIcon class="h-4 w-4" />
        </div>
        <!-- Tooltip -->
        <div class="anamnesis-tooltip">
          <div class="anamnesis-tooltip-content">Validé</div>
          <div class="anamnesis-tooltip-arrow"></div>
        </div>
      </div>
      <div v-if="showStatusBadge.showModel" class="relative group">
        <div class="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center">
          <BookTemplateIcon class="h-4 w-4" />
        </div>
        <!-- Tooltip -->
        <div class="anamnesis-tooltip">
          <div class="anamnesis-tooltip-content">Modèle</div>
          <div class="anamnesis-tooltip-arrow"></div>
        </div>
      </div>
    </div>

    <!-- Contenu principal avec padding -->
    <div class="flex-1 relative min-h-50">
      <h2 class="font-medium text-gray-800 text-lg mb-3 mt-5 p-6">
        <span :title="memo.title || memo.triid || 'Sans titre'" class="cursor-pointer">
          {{ truncateText(memo.title || memo.triid || 'Sans titre', 18) }}
        </span>
      </h2>

      <div class="space-y-2 mb-4 pl-6 pr-6">
        <p v-if="memo.material_entity" class="anamnesis-card-subtitle flex items-center gap-2">
          <Amphora class="h-5 w-5 text-gray-600" />
          <span :title="getMaterialEntityName" class="cursor-pointer">
            {{ truncateText(getMaterialEntityName, 18) }}
          </span>
        </p>
        <p v-if="memo.digital_entity_input" class="anamnesis-card-subtitle flex items-center gap-2">
          <FileInput class="h-5 w-5 text-gray-600" />
          <span :title="getDigitalEntityInputName" class="cursor-pointer">
            {{ truncateText(getDigitalEntityInputName, 18) }}
          </span>
        </p>
        <p v-if="memo.digital_entity_output" class="anamnesis-card-subtitle flex items-center gap-2">
          <FileOutput class="h-5 w-5 text-gray-600" />
          <span :title="getDigitalEntityOutputName" class="cursor-pointer">
            {{ truncateText(getDigitalEntityOutputName, 18) }}
          </span>
        </p>
        <p v-if="memo.location" class="anamnesis-card-subtitle flex items-center gap-2">
          <MapPin class="h-5 w-5 text-gray-600" />
          <span :title="getLocationName" class="cursor-pointer">
            {{ truncateText(getLocationName, 18) }}
          </span>
        </p>
        <div v-if="memo.agents_entries?.length > 0" class="anamnesis-card-subtitle flex items-start gap-2">
          <Users class="h-5 w-5 text-gray-600 mt-1" />
          <div class="ml-0.1 flex items-center gap-2 flex-wrap">
            <ul class="list-disc list-inside ml-0.1">
              <li v-for="agentEntry in displayedAgents" :key="agentEntry.agent.uuid">
                <span :title="getAgentDisplayName(agentEntry.agent)" class="cursor-pointer">
                  {{ truncateText(getAgentDisplayName(agentEntry.agent), 18) }}
                </span>
              </li>
            </ul>
            <div v-if="remainingAgentsCount > 0"
              class="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium"
              :title="`${remainingAgentsCount} agent(s) supplémentaire(s): ${remainingAgentsNames}`">
              +{{ remainingAgentsCount }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer compact -->
    <div class="px-3 py-2 border-t border-gray-200 mt-auto">
      <div class="flex items-center justify-between">
        <!-- Date à gauche -->
        <span class="flex items-center gap-2 text-sm text-gray-500">
          <Clock class="h-4 w-4 text-gray-500" />
          {{ formatDate(memo.created_at) }}
        </span>

        <!-- Boutons d'action à droite -->
        <div class="flex items-center gap-2">
          <!-- Bouton Modifier (seulement pour le propriétaire) -->
          <button v-if="showEditButton && canEdit && isOwned"
            class="relative group p-1.5 rounded hover:bg-gray-100 transition-colors duration-200 text-blue-600 hover:text-blue-700"
            aria-label="Modifier" @click.stop="handleEdit">
            <EditIcon class="h-4 w-4" />
            <div class="anamnesis-tooltip">
              <div class="anamnesis-tooltip-content">Modifier</div>
              <div class="anamnesis-tooltip-arrow"></div>
            </div>
          </button>

          <!-- Bouton Supprimer (seulement pour le propriétaire) -->
          <button v-if="showDeleteButton && canDelete && isOwned"
            class="relative group p-1.5 rounded hover:bg-gray-100 transition-colors duration-200 text-red-600 hover:text-red-700"
            aria-label="Supprimer" @click.stop="requestDeleteConfirmation(memo.uuid)">
            <TrashIcon class="h-4 w-4" />
            <div class="anamnesis-tooltip">
              <div class="anamnesis-tooltip-content">Supprimer</div>
              <div class="anamnesis-tooltip-arrow"></div>
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Mode Table -->
  <div v-else-if="viewMode === 'table'"
    class="grid grid-cols-[auto_2fr_1fr_1fr_1fr_auto_auto] cursor-pointer gap-4 sm:gap-2 p-3 sm:p-2 bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200"
    :class="{ 'selection-active': selected }"
    @click="handleCardClick">
    <!-- Checkbox de sélection -->
    <div v-if="selectable" class="flex items-center justify-center" @click.stop>
      <input 
        type="checkbox"
        :checked="selected"
        class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
        @change="emitToggleSelection"
      />
    </div>
    <!-- Type et Titre with Tooltip -->
    <div class="flex items-center gap-2 min-w-0">
      <div class="relative" @mouseenter="isIconHovered = true" @mouseleave="isIconHovered = false">
        <component :is="getTypeIcon" v-if="getTypeIcon" class="h-4 w-4 text-gray-600 shrink-0 cursor-help" />
        <!-- Custom Tooltip -->
        <div v-if="isIconHovered"
          class="absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg z-10 whitespace-nowrap">
          {{ getSchemaTooltipText }}
        </div>
      </div>
      <span :title="memo.title || memo.triid || 'Sans titre'" class="font-medium cursor-pointer">
        {{ truncateText(memo.title || memo.triid || 'Sans titre', 25) }}
      </span>

      <!-- Badge de propriété en mode table -->
      <div class="ml-2">
        <!-- Affichage pour les assets appartenant au user -->
        <div v-if="isOwned" class="relative group">
          <div v-if="!memo.is_public"
            class="w-4 h-4 rounded-full flex items-center justify-center bg-red-100 text-red-600">
            <EyeOffIcon class="w-3 h-3" />
          </div>
          <div v-else class="w-4 h-4 rounded-full flex items-center justify-center bg-green-100 text-green-600">
            <EyeIcon class="w-3 h-3" />
          </div>
        </div>
        <!-- Affichage pour les assets publics d'autres users -->
        <div v-else-if="memo.is_public" class="relative group">
          <div class="w-4 h-4 rounded-full flex items-center justify-center bg-green-100 text-green-600">
            <GlobeIcon class="w-3 h-3" />
          </div>
        </div>
      </div>
    </div>

    <!-- Material Entity -->
    <div class="hidden sm:hidden md:hidden lg:flex items-center gap-1 text-sm text-gray-600 min-w-0">
      <Amphora class="h-4 w-4 shrink-0" />
      <span :title="getMaterialEntityName" class="cursor-pointer">
        {{ truncateText(getMaterialEntityName, 15) }}
      </span>
    </div>

    <!-- Location -->
    <div class="hidden sm:hidden md:flex lg:flex items-center gap-1 text-sm text-gray-600 min-w-0">
      <MapPin class="h-4 w-4 shrink-0" />
      <span :title="getLocationName" class="cursor-pointer">
        {{ truncateText(getLocationName, 15) }}
      </span>
    </div>

    <!-- Date de création -->
    <div
      class="flex items-center gap-1 text-sm text-gray-600 whitespace-nowrap justify-self-end sm:pr-1 min-w-[3.5rem]">
      <Clock class="h-4 w-4 shrink-0" />
      <span>{{ formatDate(memo.created_at) }}</span>
    </div>

    <!-- Status Bubbles -->
    <div v-if="showStatusBubbles" class="flex items-center justify-center gap-1 min-w-[1rem]">
      <div v-if="showStatusBadge.showDraft"
        class="px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-700 flex items-center">
        <FileEditIcon class="h-3.5 w-3.5" />
      </div>
      <div v-if="showStatusBadge.showValidated"
        class="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700 flex items-center">
        <FileCheckIcon class="h-3.5 w-3.5" />
      </div>
      <div v-if="showStatusBadge.showModel"
        class="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700 flex items-center">
        <BookTemplateIcon class="h-3.5 w-3.5" />
      </div>
    </div>
    <!-- Placeholder invisible pour les status bubbles si pas affichées -->
    <div v-else class="min-w-[1rem]" aria-hidden="true"></div>

    <!-- Boutons d'action avec autorisations -->
    <div class="flex items-center gap-1 justify-self-end sm:pl-1 min-w-[1.5rem]">
      <!-- Bouton Modifier (seulement pour le propriétaire) -->
      <button v-if="showEditButton && canEdit && isOwned"
        class="p-1 rounded hover:bg-gray-100 transition-colors duration-200 text-blue-600 hover:text-blue-700"
        @click.stop="handleEdit">
        <EditIcon class="h-4 w-4" />
      </button>
      <!-- Placeholder invisible pour le bouton éditer si pas affiché -->
      <div v-else-if="showEditButton" class="p-1 opacity-0 pointer-events-none" aria-hidden="true">
        <EditIcon class="h-4 w-4" />
      </div>

      <!-- Bouton Supprimer (seulement pour le propriétaire) -->
      <button v-if="showDeleteButton && canDelete && isOwned"
        class="p-1 rounded hover:bg-gray-100 transition-colors duration-200 text-red-600 hover:text-red-700"
        @click.stop="requestDeleteConfirmation(memo.uuid)">
        <TrashIcon class="h-4 w-4" />
      </button>
      <!-- Placeholder invisible pour le bouton supprimer si pas affiché -->
      <div v-else-if="showDeleteButton" class="p-1 opacity-0 pointer-events-none" aria-hidden="true">
        <TrashIcon class="h-4 w-4" />
      </div>
    </div>
  </div>

  <!-- Modal de Confirmation -->
  <ModalAreYouSure v-model="showConfirmDeleteModal" title="Confirmer la suppression"
    message="Voulez-vous vraiment supprimer ce mémo ? Cette action est irréversible." confirm-text="Supprimer"
    cancel-text="Annuler" @confirm="handleConfirmDelete" />
</template>

<script setup lang="ts">
import type { Memo } from '@/api/types/memos/types';
import { useAuthStore } from '@/stores/auth';
import {
  Amphora,
  BookTemplate as BookTemplateIcon,
  Camera,
  Clock,
  Cog,
  Edit as EditIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  FileCheck as FileCheckIcon,
  FileEdit as FileEditIcon,
  FileInput,
  FileOutput,
  Globe as GlobeIcon,
  MapPin,
  PenTool,
  Trash as TrashIcon,
  Users
} from 'lucide-vue-next';
import { computed, onMounted, ref, watch, type PropType } from 'vue';
import { useRouter } from 'vue-router';
import { useBreadcrumbsStore } from '../../stores/breadcrumbs';
import ModalAreYouSure from '../utils/modal_areyousure.vue';

// Interface pour gérer les propriétés backend non typées
interface MemoWithBackendFields extends Memo {
  schema_model_uuid?: string;
}

const props = defineProps({
  memo: {
    type: Object as PropType<MemoWithBackendFields>,
    required: true
  },
  viewMode: {
    type: String as PropType<'card' | 'table'>,
    default: 'card',
  },
  showDeleteButton: {
    type: Boolean,
    default: true
  },
  showEditButton: {
    type: Boolean,
    default: true
  },
  showDuplicateButton: {
    type: Boolean,
    default: true
  },
  showStatusBubbles: {
    type: Boolean,
    default: true
  },
  canEdit: {
    type: Boolean,
    default: true
  },
  canDelete: {
    type: Boolean,
    default: true
  },
  selectable: {
    type: Boolean,
    default: false
  },
  selected: {
    type: Boolean,
    default: false
  },
  selectionMode: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits<{
  (e: 'view', uuid: string, memo: Memo): void;
  (e: 'edit', uuid: string, memo: Memo): void;
  (e: 'delete', uuid: string): void;
  (e: 'duplicate', uuid: string, memo: Memo): void;
  (e: 'toggle-selection', uuid: string): void;
}>();

const router = useRouter();
const breadcrumbsStore = useBreadcrumbsStore();
const authStore = useAuthStore();
const isIconHovered = ref(false);
const showConfirmDeleteModal = ref(false);
const memoToDeleteUuid = ref<string | null>(null);
const schemaInfo = ref<{ type: string; appellation: string } | null>(null);

const currentUserId = computed(() => authStore.user?.uuid || '');
const isOwned = computed(() => props.memo.user_id === currentUserId.value);

const loadSchemaInfo = async (): Promise<void> => {
  const schemaModelUuid = props.memo.schema_model_uuid;

  if (!props.memo.schema_model && schemaModelUuid) {
    try {
      const { schemas } = await import('../../api');
      const schema = await schemas.fetchSchemaById(schemaModelUuid);
      schemaInfo.value = {
        type: schema.type,
        appellation: schema.appellation
      };
    } catch (error) {
      console.error('Erreur lors du chargement des infos du schéma:', error);
    }
  } else if (props.memo.schema_model) {
    schemaInfo.value = null;
  }
};

onMounted(() => {
  loadSchemaInfo();
});

watch(() => props.memo, () => {
  loadSchemaInfo();
}, { immediate: true });

const showMemo = (uuid: string): void => {
  const memoObject = JSON.parse(JSON.stringify(props.memo));
  const memoTitle = props.memo.title || props.memo.triid || 'Sans titre';
  breadcrumbsStore.updateBreadcrumbs([{ name: memoTitle, path: `/memos/${uuid}` }]);
  history.replaceState({ memo: memoObject }, '');
  router.push(`/memos/${uuid}`);
  emit('view', uuid, memoObject);
};

const handleEdit = (): void => {
  router.push({
    path: '/form',
    query: {
      mode: 'edit',
      memoId: props.memo.uuid,
      step: '1',
      isFromDetail: 'true'
    }
  });
};

const emitToggleSelection = (): void => {
  emit('toggle-selection', props.memo.uuid);
};

const handleCardClick = (): void => {
  if (props.selectionMode) {
    emitToggleSelection();
  } else {
    showMemo(props.memo.uuid);
  }
};

const getTypeIcon = computed(() => {
  const type = (props.memo.schema_model?.type || schemaInfo.value?.type)?.toLowerCase();
  switch (type) {
    case 'acquisition': return Camera;
    case 'processing': return Cog;
    case 'analyse': return PenTool;
    default: return null;
  }
});

const getSchemaTooltipText = computed(() => {
  const type = (props.memo.schema_model?.type || schemaInfo.value?.type);
  const appellation = (props.memo.schema_model?.appellation || schemaInfo.value?.appellation);
  return type && appellation ? `${type} - ${appellation}` : 'Type de mémo inconnu';
});

const getMaterialEntityName = computed(() => props.memo.material_entity?.appellation || 'Non spécifié');
const getDigitalEntityInputName = computed(() => props.memo.digital_entity_input?.appellation || 'Non spécifié');
const getDigitalEntityOutputName = computed(() => props.memo.digital_entity_output?.appellation || 'Non spécifié');
const getLocationName = computed(() => props.memo.location?.site || 'Non spécifié');

const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const truncateText = (text: string, length: number): string => {
  if (!text) return 'Sans nom';
  return text.length > length ? `${text.substring(0, length)}...` : text;
};

const getAgentDisplayName = (agent: any): string => {
  if (!agent) return 'Sans nom';
  if (agent.is_legal_entity) {
    return agent.affiliation || 'Personne morale';
  }
  return `${agent.first_name || ''} ${agent.last_name || ''}`.trim() || 'Sans nom';
};

const displayedAgents = computed(() => props.memo.agents_entries?.slice(0, 2) || []);
const remainingAgentsCount = computed(() => Math.max(0, (props.memo.agents_entries?.length || 0) - 2));
const remainingAgentsNames = computed(() => {
  if (remainingAgentsCount.value <= 0) return '';
  return props.memo.agents_entries
    .slice(2)
    .map((entry: any) => getAgentDisplayName(entry.agent))
    .join(', ');
});

const requestDeleteConfirmation = (uuid: string): void => {
  memoToDeleteUuid.value = uuid;
  showConfirmDeleteModal.value = true;
};

const handleConfirmDelete = (): void => {
  if (memoToDeleteUuid.value) {
    emit('delete', memoToDeleteUuid.value);
  }
  showConfirmDeleteModal.value = false;
  memoToDeleteUuid.value = null;
};

const showStatusBadge = computed(() => {
  return {
    showDraft: props.memo.is_draft,
    showValidated: !props.memo.is_draft,
    showModel: props.memo.is_model,
  };
});
</script>

<style scoped>
.anamnesis-tooltip {
  visibility: hidden;
  position: absolute;
  z-index: 9999;
  top: -3rem;
  left: 50%;
  transform: translateX(-50%);
  padding: 0.25rem 0.75rem;
  pointer-events: none;
}

.anamnesis-tooltip-content {
  background-color: rgb(17, 24, 39);
  color: white;
  font-size: 0.75rem;
  border-radius: 0.375rem;
  padding: 0.5rem;
  white-space: nowrap;
}

.anamnesis-tooltip-arrow {
  position: absolute;
  left: 50%;
  bottom: -0.25rem;
  transform: translateX(-50%) rotate(45deg);
  width: 1rem;
  height: 1rem;
  background-color: rgb(17, 24, 39);
}

.group:hover .anamnesis-tooltip {
  visibility: visible;
}

.anamnesis-card {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background-color: white;
}
.anamnesis-card.selection-active {
  border-color: #3b82f6; /* blue-500 */
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.4);
}

.anamnesis-table.selection-active {
  border-color: #3b82f6; /* blue-500 */
  box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.4);
}
</style>